import ScheduleDialog from "@/dialogs/ScheduleDialog"
import useDialogState from "@/hooks/useDialogState"
import useIsMobile from "@/hooks/useIsMobile"
import { Box, Button, Card, CardContent, Typography } from "@mui/material"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

interface ServiceCardProps {
  service1: string
  service2: string
  image: string
  description: string
}

export default function ServiceCard({
  service1,
  service2,
  image,
  description,
}: ServiceCardProps) {
  const isMobile = useIsMobile()
  const location = useLocation()
  const navigate = useNavigate()
  const [isFlipped, setIsFlipped] = useState(false)
  const handleFlip = () => {
    setIsFlipped(!isFlipped)
  }

  const { openDialog, ...dialogState } = useDialogState({})

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.has("SEO") && service1 === "SEO" && !dialogState.isOpen) {
      openDialog({})
    } else if (
      params.has("Social") &&
      service1 === "Social Media" &&
      !dialogState.isOpen
    ) {
      openDialog({})
    } else if (
      params.has("Website") &&
      service1 === "Website" &&
      !dialogState.isOpen
    ) {
      openDialog({})
    } else if (
      params.has("Video") &&
      service1 === "Video" &&
      !dialogState.isOpen
    ) {
      openDialog({})
    }
  }, [location.search, openDialog, service1, dialogState.isOpen])

  const handleClose = () => {
    dialogState.closeDialog()
    navigate(location.pathname)
  }

  return (
    <Box
      sx={{
        perspective: "1000px",
        width: "100%",
        height: isMobile ? "250px" : "400px",
        margin: "auto",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          transformStyle: "preserve-3d",
          transition: "transform 0.6s",
          transform: isFlipped ? "rotateY(180deg)" : "rotateY(0)",
        }}
      >
        {/* Front Side */}
        <Card
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            backfaceVisibility: "hidden",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
          }}
          onClick={handleFlip}
        >
          {/* Black Banner */}
          <Box
            sx={{
              position: "absolute",
              bottom: "20px",
              left: "0",
              width: "90%",
              height: isMobile ? "54px" : "65px",
              backgroundColor: "#333",
              clipPath: "polygon(0 0, 90% 0, 100% 100%, 0% 100%)",
              zIndex: 2,
              paddingBottom: isMobile ? "0px" : "8px",
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              fontSize={isMobile ? "20px" : "38px"}
              sx={{
                color: "white",
                zIndex: 1,
                paddingLeft: isMobile ? 1 : 2,
                lineHeight: 1,
                paddingTop: 1,
              }}
            >
              {service1}
            </Typography>
            <Typography
              variant="subtitle1"
              fontSize={isMobile ? "16px" : "24px"}
              sx={{
                color: "white",
                zIndex: 1,
                paddingLeft: isMobile ? 1 : 2,
                lineHeight: 1,
              }}
            >
              {service2}
            </Typography>
          </Box>
          <CardContent>
            <img
              src={image}
              alt={`${service1} ${service2}`}
              style={{
                width: isMobile ? "136px" : "270px",
                height: isMobile ? "126px" : "250px",
              }}
            />
          </CardContent>
        </Card>

        {/* Back Side */}
        <Card
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            backfaceVisibility: "hidden",
            zIndex: 1,
            transform: "rotateY(180deg)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            backgroundColor: "white",
            color: "black",
            borderRadius: "16px",
            boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
          }}
          onClick={handleFlip}
        >
          <CardContent>
            <Typography
              variant="h1"
              fontWeight="bold"
              fontSize={isMobile ? "20px" : "38px"}
              sx={{ mb: isMobile ? 0 : 1, textAlign: "center" }}
            >
              {service1}
            </Typography>
            <Typography
              variant="h3"
              fontSize={isMobile ? "16px" : "24px"}
              sx={{ mb: isMobile ? 1 : 2, textAlign: "center" }}
            >
              {service2}
            </Typography>
            <Typography
              variant="body1"
              fontSize={isMobile ? "12px" : "20px"}
              sx={{
                mb: isMobile ? 1 : 3,
                px: isMobile ? 0 : 2,
                textAlign: "center",
              }}
            >
              {description}
            </Typography>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#147BE1",
                borderRadius: 8,
                paddingX: 3,
                pointerEvents: "auto",
                position: "relative",
              }}
              onClick={(e) => {
                e.stopPropagation()
                openDialog({})
              }}
            >
              Schedule
            </Button>
          </CardContent>
        </Card>
      </Box>
      <ScheduleDialog closeDialog={handleClose} isOpen={dialogState.isOpen} />
    </Box>
  )
}
